import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const IlBiuntPage = () => {
  const { t } = useTranslation();

  // Ottieni i dati dal file di traduzioni
  const viniData = t("vini.lista_vini", { returnObjects: true }) || [];
  const wineData = viniData[0] || {}; // Il Biunt è il primo vino
  const [selectedImage, setSelectedImage] = useState(null);

  const sections = [
    {
      descIndex: 0,
      image: "/biunt_bottiglia.png",
    },
    {
      descIndex: 1,
      image: "/beverahills_biunt_calice.jpg",
    },
    {
      descIndex: 2,
      image: "/biunt-bicchiere.png",
    },
    {
      descIndex: 3,
      image: "/beverahills_biunt_cartone.jpg",
    },
  ];

  return (
    <div className="md:max-w-[80%] 3xl:max-w-full mx-auto">
      <div className="container mx-auto px-4">
        {/* Header */}
        
          <h1 className="text-5xl font-light titolo text-center text-gray-900 mb-2">
            {wineData.name}.
          </h1>
          <p className="text-xl text-center text-gray-600">
            {wineData.denominazione}
          </p>

        {/* Sezioni alternate immagine-testo */}
        {sections.map((section, index) => {
          const desc = wineData.descriptions?.[section.descIndex] || "";
          const parts = desc.split(/(Al naso|Al palato)/g);

          return (
            <motion.div
              key={index}
              className={`flex flex-col md:flex-row items-center py-8 ${
                index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
              }`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img
                src={section.image}
                alt={wineData.name}
                className="w-full md:w-1/2 rounded-2xl shadow-lg"
              />
              <div className="w-full md:w-1/2 p-6 md:ml-6 md:mr-6 shadow-md rounded-xl">
                <p className="text-lg text-gray-700 font-medium leading-relaxed whitespace-pre-line">
                  {parts.map((part, i) =>
                    part === "Al naso" || part === "Al palato" ? (
                      <strong key={i}>{part}</strong>
                    ) : (
                      part
                    )
                  )}
                </p>
              </div>
            </motion.div>
          );
        })}

        {/* Sezione Scheda Info Principale */}
        <motion.div
          className="border-t border-gray-200 pt-16 mb-20"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-light text-gray-900 mb-12">
            Informazioni Principali
          </h2>

          <div className="space-y-5 p-8 bg-gray-50 rounded-xl">
            {[
              { label: "Denominazione", value: wineData.denominazione },
              { label: "Vitigno", value: wineData.vitigno },
              { label: "Annata", value: wineData.annata },
              { label: "Alcool", value: wineData.gradazione },
              { label: "Formato", value: wineData.size },
              { label: "Temperatura servizio", value: wineData.temperaturaServizio },
            ].map((item, idx) => (
              <div key={idx} className="flex justify-between flex-col md:flex-row items-start border-b border-gray-200 pb-3">
                <span className="text-gray-600 font-medium">{item.label}</span>
                <span className="text-gray-900 text-right">{item.value}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Sezione Scheda Tecnica Completa */}
        <motion.div
          className="border-t border-gray-200 pt-16  mb-20 "
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-light text-gray-900 mb-12">
            Scheda Tecnica
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
            {/* Colonna 1 */}
            <div className="space-y-8">
              {[
                { label: "Zona di Produzione", value: wineData.zona },
                { label: "Vigneto", value: wineData.vigneto },
                { label: "Numero Piante", value: wineData.qtaPiante },
                { label: "Sesto d'impianto", value: wineData.sestoImpianto },
                { label: "Densità dell'impianto", value: wineData.densita },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  viewport={{ once: true }}
                >
                  <p className=" tracking-wider text-gray-600 font-medium">
                    {item.label}
                  </p>
                  <p className="text-gray-900 text-base">{item.value}</p>
                </motion.div>
              ))}
            </div>

            {/* Colonna 2 */}
            <div className="space-y-8">
              {[
                { label: "Forma d'allevamento", value: wineData.formaAllevamento },
                { label: "Vendemmia", value: wineData.vendemmia },
                { label: "Vinificazione", value: wineData.vinificazione },
                { label: "Affinamento", value: wineData.affinamento },
                { label: "Produzione annuale", value: wineData.produzione },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  viewport={{ once: true }}
                >
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">
                    {item.label}
                  </p>
                  <p className="text-gray-900 text-base">{item.value}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Sezione Caratteristiche Organolettiche */}
        <motion.div
          className="border-t border-gray-200 pt-16 mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-light text-gray-900 mb-12">
            Caratteristiche Organolettiche
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Sentori Aromatici */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <p className="tracking-wider text-gray-600 font-medium mb-6">
                Sentori Aromatici
              </p>
              <ul className="space-y-3">
                {wineData.aromiPrimari &&
                  wineData.aromiPrimari.map((aroma, idx) => (
                    <li key={idx} className="text-gray-900">
                      • {aroma}
                    </li>
                  ))}
              </ul>
            </motion.div>

            {/* Abbinamenti */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <p className=" tracking-wider text-gray-600 font-medium mb-6">
                Abbinamenti Consigliati
              </p>
              <ul className="space-y-3">
                {wineData.abbinamenti &&
                  wineData.abbinamenti.map((abbinamento, idx) => (
                    <li key={idx} className="text-gray-900">
                      • {abbinamento}
                    </li>
                  ))}
              </ul>
            </motion.div>
          </div>
        </motion.div>

         <motion.div
          className="border-t border-gray-200 pt-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-light text-gray-900 mb-12">
            Riconoscimenti
          </h2>

<div className="flex md:flex-row flex-col justify-center gap-6 px-4 mt-10">
             
                <motion.img
                  key={`Diploma_prosit_Beverahills_Biunt.svg`}
                  src="/Diploma_prosit_Beverahills_Biunt.svg"
                  alt={`Diploma Prosit Biunt`}
                  className="rounded-xl shadow-lg border border-gray-200 transform hover:scale-105 transition-transform duration-200 max-h-[400px] cursor-pointer"
                  onClick={() => setSelectedImage(`/Diploma_prosit_Beverahills_Biunt.svg`)}
                  whileHover={{ scale: 1.05 }}
                />
              
            </div>

          </motion.div>
           {/* Overlay fullscreen */}
            <AnimatePresence>
              {selectedImage && (
                <motion.div
                  className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 cursor-pointer"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setSelectedImage(null)}
                >
                  <motion.img
                    src={selectedImage}
                    alt="Diploma ingrandito"
                    className="max-h-[90vh] max-w-[90vw] rounded-xl shadow-2xl border border-white"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 200, damping: 20 }}
                  />
                </motion.div>
              )}
            </AnimatePresence>

      </div>
    </div>
  );
};

export default IlBiuntPage;